import { useQuery } from '@apollo/client';
import { faInfoCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import invariant from 'tiny-invariant';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import { DiagramParentType } from '~/apollo/generated/v4/graphql';
import { Panel, SimplePanel } from '~/components/common/Panel';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { DiagramRendererV4 } from '~/components/diagram/diagram-renderer-v4';
import { Alert } from '~/components/ui/alert';
import { Button } from '~/components/ui/button';
import { CollapsibleCard } from '~/components/ui/collapse';
import { DeleteDiagram } from '~/components/upload/diagram/delete-diagram';
import { DiagramEditor } from '~/components/upload/diagram/diagram-editor';
import { UploadDiagramImage } from '~/components/upload/diagram/upload-diagram-image';
import { useRouteParam } from '~/hooks/routing';
import { createRefetchQueries } from '~/utils/graphql';

const UPLOAD_DEPOSITIONAL_UPDATE_DIAGRAM_PAGE = graphql(`
  query UploadDepositionalUpdateDiagramPage($id: ID!) {
    depositionalWikiGet(id: $id) {
      id
      diagram {
        ...diagramEditorParts
      }
    }
  }
`);

export default function UploadDepositionalUpdateDiagramPage() {
  return (
    <ApolloProviderV4>
      <UploadDepositionalUpdateDiagramPageInner />
    </ApolloProviderV4>
  );
}

function UploadDepositionalUpdateDiagramPageInner() {
  const wikiId = useRouteParam('depositionalId', parseInt);

  const { data, loading } = useQuery(UPLOAD_DEPOSITIONAL_UPDATE_DIAGRAM_PAGE, {
    variables: { id: wikiId },
  });

  const refetchQueries = createRefetchQueries([
    {
      query: UPLOAD_DEPOSITIONAL_UPDATE_DIAGRAM_PAGE,
      variables: { id: wikiId },
    },
  ]);

  const wikiPage = data?.depositionalWikiGet;
  const diagram = wikiPage?.diagram;

  if (loading) return <SpinnerPlaceholder />;
  invariant(wikiPage, 'Error loading depositional wiki');

  if (!diagram) {
    return (
      <div className="space-y-2">
        <SimplePanel title="Create a Diagram">
          <div className="space-y-4">
            <Alert>
              <FontAwesomeIcon icon={faInfoCircle} />
              <div>
                This wiki page doesn't have a diagram yet. Get started by
                uploading one below.
              </div>
            </Alert>

            <UploadDiagramImage
              parentType={DiagramParentType.DepositionalWiki}
              parentId={wikiId}
              refetchQueries={refetchQueries}
            />
          </div>
        </SimplePanel>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DiagramEditor diagram={diagram} />

      <Panel>
        <Panel.Heading>
          <Panel.Title>Preview</Panel.Title>
        </Panel.Heading>
        <Panel.Body>
          <DiagramRendererV4 diagram={diagram} />
        </Panel.Body>
      </Panel>

      <CollapsibleCard title="Delete Diagram">
        <div className="text-center">
          <DeleteDiagram diagramId={diagram.id} refetchQueries={refetchQueries}>
            {confirmDelete => (
              <Button
                type="button"
                onClick={confirmDelete}
                color="error"
                startIcon={<FontAwesomeIcon icon={faTrash} />}
              >
                Delete Diagram
              </Button>
            )}
          </DeleteDiagram>
        </div>
      </CollapsibleCard>
    </div>
  );
}
